/**
 * Generic data table.
 *
 * Every list screen in the app is this component plus a column array. It owns sorting
 * indicators, selection, density, column visibility, empty/loading/error states,
 * pagination, sticky headers and row urgency tinting — so a page never re-implements
 * any of them, and they behave identically everywhere.
 *
 * Server-side by design: sorting and paging are delegated to `ListState` (which lives
 * in the URL), because the server is already doing the filtering and a 20k-row book
 * must never be shipped to the browser to be sorted.
 */

import * as React from 'react';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
  type Row,
  type VisibilityState,
} from '@tanstack/react-table';
import {
  ArrowDown,
  ArrowUp,
  ChevronsUpDown,
  Inbox,
  type LucideIcon,
  RotateCw,
  SlidersHorizontal,
  TriangleAlert,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Checkbox, Skeleton } from '@/components/ui/misc';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { FilterControl, type FilterDescriptor } from '@/components/filters/filter-bar';
import { useI18n } from '@/lib/i18n';
import { usePreferences } from '@/lib/preferences';
import { cn } from '@/lib/utils';
import type { ListState } from '@/hooks/use-list-state';

import { DataTablePagination } from './pagination';

/** Extra column metadata this table understands, layered onto TanStack's ColumnDef. */
export interface DataColumnMeta {
  /** Sort key sent to the API. Presence of this makes the header clickable. */
  sortKey?: string;
  align?: 'left' | 'right' | 'center';
  /** Numeric columns get tabular figures so digits line up down the column. */
  numeric?: boolean;
  /** Hidden below this breakpoint; keeps narrow screens readable without a h-scroll. */
  hideBelow?: 'sm' | 'md' | 'lg' | 'xl';
  headerLabel?: string;
  width?: string;
}

export type DataColumn<T> = ColumnDef<T> & { meta?: DataColumnMeta };

export interface DataTableProps<T> {
  columns: DataColumn<T>[];
  rows: T[] | undefined;
  isLoading?: boolean;
  isFetching?: boolean;
  error?: { message: string } | null;
  onRetry?: () => void;
  getRowId: (row: T) => string;
  onRowClick?: (row: T) => void;
  /** Applies a tint utility class (`row-tint-*`) for urgency banding. */
  rowClassName?: (row: T) => string | undefined;
  listState?: ListState;
  total?: number;
  /**
   * The page's filter descriptors. Any of them carrying a `column` is rendered in a filter
   * row under the header, beneath the column it narrows; the rest are the toolbar's
   * business. Requires `listState`, which is where a filter is actually stored.
   */
  filters?: FilterDescriptor[];
  /** Enables the selection column and the bulk-action bar. */
  selection?: {
    selectedIds: string[];
    onChange: (ids: string[]) => void;
    /** Rendered in the bar that appears while rows are selected. */
    actions?: React.ReactNode;
  };
  emptyTitle?: string;
  emptyHint?: string;
  emptyAction?: React.ReactNode;
  /** Sticky summary row, e.g. filtered premium totals. */
  footer?: React.ReactNode;
  stickyHeader?: boolean;
  className?: string;
}

export function DataTable<T>({
  columns,
  rows,
  isLoading,
  isFetching,
  error,
  onRetry,
  getRowId,
  onRowClick,
  rowClassName,
  listState,
  total,
  filters,
  selection,
  emptyTitle,
  emptyHint,
  emptyAction,
  footer,
  stickyHeader = true,
  className,
}: DataTableProps<T>) {
  const { t, tn } = useI18n();
  const { density } = usePreferences();
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({});

  const selectedSet = React.useMemo(() => new Set(selection?.selectedIds ?? []), [selection?.selectedIds]);

  const allColumns = React.useMemo<DataColumn<T>[]>(() => {
    if (!selection) return columns;

    const selectColumn: DataColumn<T> = {
      id: '__select',
      meta: { width: '2.5rem' },
      header: () => {
        const ids = (rows ?? []).map(getRowId);
        const allSelected = ids.length > 0 && ids.every((id) => selectedSet.has(id));
        const someSelected = ids.some((id) => selectedSet.has(id));

        return (
          <Checkbox
            aria-label={t('action.selectAll')}
            checked={allSelected}
            indeterminate={!allSelected && someSelected}
            onCheckedChange={(checked) => {
              // Union/difference rather than replacement, so selections made on a
              // previous page survive paging back and forth.
              const next = new Set(selectedSet);
              if (checked) ids.forEach((id) => next.add(id));
              else ids.forEach((id) => next.delete(id));
              selection.onChange([...next]);
            }}
          />
        );
      },
      cell: ({ row }) => {
        const id = getRowId(row.original);
        return (
          <Checkbox
            aria-label={id}
            checked={selectedSet.has(id)}
            onClick={(event) => event.stopPropagation()}
            onCheckedChange={(checked) => {
              const next = new Set(selectedSet);
              if (checked) next.add(id);
              else next.delete(id);
              selection.onChange([...next]);
            }}
          />
        );
      },
    };

    return [selectColumn, ...columns];
  }, [columns, selection, rows, getRowId, selectedSet, t]);

  const table = useReactTable({
    data: rows ?? [],
    columns: allColumns,
    getCoreRowModel: getCoreRowModel(),
    state: { columnVisibility },
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    manualFiltering: true,
  });

  // Rows breathe. Comfortable goes to 54px so a name, a badge and a 24px avatar all sit
  // in the row with air above and below rather than being clamped by it; compact stays
  // genuinely dense for the users who page through hundreds of rows a day.
  const cellPadding = density === 'compact' ? 'h-10 py-1.5' : 'h-[3.375rem] py-2.5';
  const hideClass: Record<string, string> = {
    sm: 'hidden sm:table-cell',
    md: 'hidden md:table-cell',
    lg: 'hidden lg:table-cell',
    xl: 'hidden xl:table-cell',
  };

  const columnCount = table.getVisibleFlatColumns().length;
  const hasRows = (rows?.length ?? 0) > 0;

  /*
   * Column id → its filter. Built from the descriptors the page already passes to its
   * toolbar, so a filter is declared once and appears in exactly one place: under its
   * column here, or in the toolbar when it has no column to sit under.
   */
  const columnFilters = React.useMemo(() => {
    const map = new Map<string, FilterDescriptor>();
    for (const filter of filters ?? []) {
      if (filter.column) map.set(filter.column, filter);
    }
    return map;
  }, [filters]);

  // Only when a filter actually lands on a visible column — an empty strip of cells under
  // the header is a row of nothing that costs 38px on every screen that has no filters.
  const showFilterRow =
    listState !== undefined &&
    columnFilters.size > 0 &&
    table.getVisibleLeafColumns().some((column) => columnFilters.has(column.id));

  return (
    <div className={cn('flex flex-col', className)}>
      {/* Selection is one of the four jobs blue keeps, so the bulk bar is allowed a
          tint — but a faint one, since it appears directly above tinted rows. */}
      {selection && selection.selectedIds.length > 0 && (
        <div className="bg-primary/[0.06] border-primary/20 mb-3.5 flex flex-wrap items-center gap-3 rounded-xl border px-3.5 py-2.5">
          <span className="text-[13px] font-medium">{tn('count.selected', selection.selectedIds.length)}</span>
          <div className="flex flex-wrap items-center gap-2">{selection.actions}</div>
          <Button variant="ghost" size="sm" className="ml-auto" onClick={() => selection.onChange([])}>
            {t('action.clear')}
          </Button>
        </div>
      )}

      <div className="bg-card border-border/70 relative overflow-hidden rounded-xl border shadow-sm">
        {/* A refetch tints the surface instead of unmounting the table, so the rows
            never flash away while a filter is being adjusted. */}
        {isFetching && !isLoading && (
          <div className="bg-primary/60 absolute inset-x-0 top-0 z-20 h-0.5 animate-pulse" />
        )}

        <Table>
          <TableHeader className={cn(stickyHeader && 'sticky top-0 z-10')}>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="hover:bg-transparent">
                {headerGroup.headers.map((header) => {
                  const meta = header.column.columnDef.meta as DataColumnMeta | undefined;
                  const sortKey = meta?.sortKey;
                  const isSorted = listState?.sortBy === sortKey;

                  return (
                    <TableHead
                      key={header.id}
                      style={meta?.width ? { width: meta.width } : undefined}
                      className={cn(
                        meta?.align === 'right' && 'text-right',
                        meta?.align === 'center' && 'text-center',
                        meta?.hideBelow && hideClass[meta.hideBelow],
                      )}
                      aria-sort={isSorted ? (listState?.sortOrder === 'asc' ? 'ascending' : 'descending') : undefined}
                    >
                      {header.isPlaceholder ? null : sortKey && listState ? (
                        <button
                          type="button"
                          onClick={() => listState.setSort(sortKey)}
                          className={cn(
                            'group hover:text-foreground -mx-1.5 inline-flex items-center gap-1 rounded-md px-1.5 py-1',
                            'transition-colors duration-150',
                            meta?.align === 'right' && 'flex-row-reverse',
                            isSorted && 'text-foreground',
                          )}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {/* The unsorted chevron only surfaces on hover — twelve of them
                              standing permanently in the header row is visual static. */}
                          {isSorted ? (
                            listState.sortOrder === 'asc' ? (
                              <ArrowUp className="size-3" />
                            ) : (
                              <ArrowDown className="size-3" />
                            )
                          ) : (
                            <ChevronsUpDown className="size-3 opacity-0 transition-opacity duration-150 group-hover:opacity-40" />
                          )}
                        </button>
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}

            {/*
              The filter row, wide screens only.
              Below `lg` the same controls are in the toolbar's popover: a column filter is
              only meaningful next to its column, and half the columns are hidden there.
            */}
            {showFilterRow && (
              <TableRow className="hover:bg-transparent">
                {table.getVisibleLeafColumns().map((column) => {
                  const meta = column.columnDef.meta as DataColumnMeta | undefined;
                  const filter = columnFilters.get(column.id);

                  return (
                    /*
                     * `<td>`, not `<th>`, inside the head. A second row of `<th>` would make
                     * every column claim two headers, so a screen reader announces the filter's
                     * own label as part of each data cell's heading. Opaque, too: the whole
                     * `<thead>` is sticky, and a translucent row lets the data scroll visibly
                     * through the controls that filter it.
                     */
                    <TableCell
                      key={`filter-${column.id}`}
                      className={cn(
                        'bg-card border-border border-b px-2 py-1.5 align-middle first:pl-4 last:pr-4',
                        // The row itself starts at `lg`; a column hidden until `xl` keeps its
                        // own, later, breakpoint so the filter never outlives its column.
                        meta?.hideBelow === 'xl' ? hideClass.xl : 'hidden lg:table-cell',
                      )}
                    >
                      {filter && listState ? (
                        <FilterControl filter={filter} listState={listState} compact />
                      ) : null}
                    </TableCell>
                  );
                })}
              </TableRow>
            )}
          </TableHeader>

          <TableBody>
            {isLoading ? (
              <SkeletonRows columns={columnCount} rows={8} className={cellPadding} />
            ) : error ? (
              <StateRow columns={columnCount}>
                <StateIcon icon={TriangleAlert} tone="critical" />
                <p className="mt-1 font-medium">{t('common.error')}</p>
                <p className="text-muted-foreground max-w-md text-[13px]">{error.message}</p>
                {onRetry && (
                  <Button variant="outline" size="sm" onClick={onRetry} className="mt-2">
                    <RotateCw /> {t('action.retry')}
                  </Button>
                )}
              </StateRow>
            ) : !hasRows ? (
              <StateRow columns={columnCount}>
                <StateIcon icon={Inbox} />
                <p className="mt-1 font-medium">{emptyTitle ?? t('common.noResults')}</p>
                <p className="text-muted-soft max-w-sm text-[13px] leading-relaxed">
                  {emptyHint ?? t('common.noResultsHint')}
                </p>
                {emptyAction && <div className="mt-2">{emptyAction}</div>}
              </StateRow>
            ) : (
              table.getRowModel().rows.map((row: Row<T>) => (
                <TableRow
                  key={row.id}
                  data-state={selectedSet.has(getRowId(row.original)) ? 'selected' : undefined}
                  onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                  className={cn(
                    onRowClick && 'cursor-pointer',
                    rowClassName?.(row.original),
                    'last:[&>td]:border-b-0',
                  )}
                >
                  {row.getVisibleCells().map((cell) => {
                    const meta = cell.column.columnDef.meta as DataColumnMeta | undefined;
                    return (
                      <TableCell
                        key={cell.id}
                        className={cn(
                          cellPadding,
                          meta?.align === 'right' && 'text-right',
                          meta?.align === 'center' && 'text-center',
                          meta?.numeric && 'tabular',
                          meta?.hideBelow && hideClass[meta.hideBelow],
                        )}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {/*
          The footer sums the whole filtered set, not the visible page — which is what an
          agent wants from "what is this book worth", but the pagination directly above it
          reads "1–25 of 40", so the two invited exactly the wrong inference. Saying which
          basis is in play costs one label.
        */}
        {footer && hasRows && (
          <div className="bg-muted/50 border-border flex flex-wrap items-center justify-between gap-x-7 gap-y-1 border-t px-5 py-3 text-[13px]">
            <span className="text-muted-soft text-[11.5px]">
              {total !== undefined ? tn('table.footerBasis', total) : ''}
            </span>
            <div className="flex flex-wrap items-center justify-end gap-7">{footer}</div>
          </div>
        )}
      </div>

      {listState && total !== undefined && (
        <DataTablePagination listState={listState} total={total} loaded={rows?.length ?? 0} />
      )}
    </div>
  );
}

function SkeletonRows({ columns, rows, className }: { columns: number; rows: number; className: string }) {
  return (
    <>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <TableRow key={rowIndex} className="hover:bg-transparent">
          {Array.from({ length: columns }).map((__, columnIndex) => (
            <TableCell key={columnIndex} className={className}>
              <Skeleton
                className="h-3.5"
                style={{ width: `${[70, 55, 80, 45, 65, 50][columnIndex % 6]}%` }}
              />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}

function StateRow({ columns, children }: { columns: number; children: React.ReactNode }) {
  return (
    <TableRow className="hover:bg-transparent">
      <TableCell colSpan={columns} className="border-b-0">
        <div className="flex flex-col items-center justify-center gap-1.5 px-6 py-20 text-center">
          {children}
        </div>
      </TableCell>
    </TableRow>
  );
}

/**
 * The icon in an empty state, set in a soft disc.
 *
 * A bare 28px outline glyph floating in whitespace reads as a rendering accident; the
 * disc gives it a deliberate footprint and lets the icon itself stay small and quiet,
 * which is what keeps an empty table looking designed rather than broken.
 */
function StateIcon({ icon: Icon, tone = 'neutral' }: { icon: LucideIcon; tone?: 'neutral' | 'critical' }) {
  return (
    <span
      className={cn(
        'mb-1 grid size-11 place-items-center rounded-full',
        tone === 'critical' ? 'bg-[var(--tint-critical-bg)] text-critical' : 'bg-foreground/[0.045] text-muted-soft',
      )}
      aria-hidden
    >
      <Icon className="size-5" />
    </span>
  );
}

/** Column-visibility menu — rendered by pages next to their filter bar. */
export function ColumnToggle<T>({ table }: { table: ReturnType<typeof useReactTable<T>> }) {
  const { t } = useI18n();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm">
          <SlidersHorizontal /> {t('common.columns')}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        <DropdownMenuLabel>{t('common.columns')}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {table
          .getAllLeafColumns()
          .filter((column) => column.getCanHide() && column.id !== '__select')
          .map((column) => {
            const meta = column.columnDef.meta as DataColumnMeta | undefined;
            return (
              <DropdownMenuCheckboxItem
                key={column.id}
                checked={column.getIsVisible()}
                onCheckedChange={(value) => column.toggleVisibility(Boolean(value))}
                onSelect={(event) => event.preventDefault()}
              >
                {meta?.headerLabel ?? column.id}
              </DropdownMenuCheckboxItem>
            );
          })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
